// Die Bauteile der Basis — je Ausbaustufe eines (VIEW-001, Gate FG-45).
//
// Der Ausbau ist der **Takt der Progression**: dreizehn Stufen ueber zwei Akte,
// jede mit Namen, Beschreibung und einem eigenen Ast im Baum. An der Kapsel war er
// bis hierher nur eine Zahl — dieselbe Silhouette, nur groesser. Wer die
// Werkstatt baute, sah einen etwas groesseren grauen Kasten; wer den Sensorturm
// baute, denselben Kasten, noch etwas groesser. Es ist dasselbe Argument, das
// schon die sechs Schiffsbilder getragen hat (`SPRITES.md`).
//
// Jedes Bauteil steht **zweimal** hier: als `frame` in `base.png` und als
// `draw`. Das Bild gewinnt; die Zeichnung ist der Rueckfall, wenn die Datei
// fehlt — dieselbe Regel wie bei Schiff und Brocken. Sie zu behalten kostet
// nichts und traegt zwei Dinge: das Spiel startet ohne die Datei, und FG-45 kann
// jede Form weiterhin **ausfuehren**.
//
// Diese Datei importiert **kein Phaser**. Das ist Absicht, wie bei `sprites.ts`
// und `icons.ts`: so kann ein Test die Registry nicht nur lesen, sondern jede
// Form auch ausfuehren — gegen einen aufzeichnenden Kontext, der nachrechnet,
// dass nichts aus seiner Leinwand ragt.
//
// **Ein fehlendes Bauteil faellt still aus.** Es gibt keinen Rueckfall und kein
// Ersatzsymbol: eine unbekannte Build-ID zeichnet einfach nichts, und an der
// Station steht dann eine bezahlte Stufe, die niemand sieht. Dieselbe Falle wie
// beim Icon-Namen (FG-25) und beim Sprite-Namen (FG-40), eine Ebene weiter —
// und genau deshalb bewacht sie ein Gate statt eines Platzhalters. Ein `throw`
// beim Start waere falsch: die Tabellen kommen zur Laufzeit **vom Server**, und
// ein Server, der dem Client voraus ist, darf keinen Browser am Start hindern.

import { TAU } from '../core/math';
import { builds, eras } from '../data/balancing';
import { cssColor } from './palette';

/** Kantenlaenge einer Modultextur. 13 Stueck kosten damit rund 850 KB. */
export const BASE_MODULE_SIZE = 128;

/**
 * Grenze, innerhalb derer eine Form bleiben muss.
 *
 * Nicht die halbe Leinwand: eine Linie mit `lineWidth` 4 traegt zwei Pixel nach
 * aussen, und ein beschnittenes Bauteil sieht aus wie ein Zeichenfehler, nicht
 * wie eine zu grosse Form. Gate FG-45 misst dagegen.
 */
export const BASE_MOTIF_REACH = 56;

/** Rumpfradius auf Stufe 0 — die nackte Kapsel. */
export const BASE_HULL_R0 = 84;

/**
 * Zuwachs des Rumpfes je Stufe.
 *
 * Bewusst klein. Vorher waren es 15 Einheiten, und die Groesse war die
 * **einzige** Aussage ueber die Stufe; jetzt tragen die Bauteile sie. Waechst
 * der Rumpf weiter kraeftig, muessten die Anker mitwandern — und dann schoebe
 * jede neue Stufe alle bisherigen nach aussen. Der Spieler saehe nicht „hier
 * ist etwas dazugekommen", sondern „alles hat sich verschoben".
 *
 * Mit der Linsenkette von 4 auf 3 gesenkt, mit der Navigationskette auf 2: elf
 * Stufen in Akt I hielten sonst `BASE_HULL_LIMIT` nicht mehr ein, und der Rumpf
 * verschlaenge die Anker. Das ist der Preis dafuer, dass die Kette waechst und
 * die Anker feststehen — und er ist der richtige herum: die Bauteile tragen die
 * Aussage, nicht die Groesse.
 */
export const BASE_HULL_GROW = 2;

/** Verhaeltnis von halber Hoehe zu Radius — unveraendert aus der ersten Fassung. */
export const BASE_HULL_ASPECT = 0.62;

/**
 * Grenze, die der Rumpf nie erreichen darf.
 *
 * Die Anker der Flanken liegen dahinter. Waechst der Rumpf darueber hinaus,
 * verschluckt er die Bauteile — und der Ausbau waere wieder nur eine Groesse.
 */
export const BASE_HULL_LIMIT = 112;

/** Der Ring der abgeschlossenen Kette. Fest, aus demselben Grund. */
export const BASE_FINAL_RING = 198;

/** Der Truemmerring von Akt II. Fest, aus demselben Grund. */
export const BASE_DEBRIS_RING = 212;

/** Wie viele Truemmerbrocken eine unberuehrte Ruine hat. */
export const BASE_DEBRIS_COUNT = 14;

// ── Die Farben der Station ───────────────────────────────────────────────────
//
// Sie stehen hier und nicht in `palette.ts`: dort liegt die Tabelle der
// **Feld**-Tints, gegen die FG-42 den Abstand der Erzfarben misst („alles, was
// neben einem Brocken im selben Bild leuchtet"). Die Basis steht an genau einem
// Ort, an dem niemand ein Erz identifizieren muss; dreizehn zusaetzliche Toene
// wuerden die Erzpalette grundlos einschnueren.
//
// Der eigentliche Grund fuer „Farben nicht in der Szene" ist ein anderer: dort
// sind sie hinter Phaser fuer jedes Gate unerreichbar. Diese Datei ist es nicht.
// `icons.ts` haelt Form und Farbe seit jeher in derselben Zeile.

export const BASE_HULL_TINT = 0xcdc6b8;
export const BASE_EDGE_TINT = 0x8e8778;
export const BASE_SHADOW_TINT = 0x2b3038;
export const BASE_DOCK_TINT = 0x2a4d4a;
export const BASE_IDLE_TINT = 0x1d2a30;
export const BASE_DEBRIS_TINT = 0x3a3630;

const HULL = cssColor(BASE_HULL_TINT);
const EDGE = cssColor(BASE_EDGE_TINT);
const SHADOW = cssColor(BASE_SHADOW_TINT);
const LIGHT = '#e8e2d4';
const RUST = '#6a6157';

export interface BaseModule {
  /** Ankerpunkt in Weltkoordinaten, relativ zum Ursprung. Fest — Module wandern nie. */
  x: number;
  y: number;
  /**
   * Platz im Bauteilraster `base.png` — oder `null`.
   *
   * **Eindeutig, wo vorhanden** — zwei Stufen auf demselben Frame saehen gleich
   * aus, ohne dass irgendetwas scheitert. Genau die stille Sorte Fehler, gegen
   * die es FG-45 gibt, und deshalb prueft es die Eindeutigkeit.
   *
   * `null` heisst: **das Blatt hat keinen Platz mehr.** Es ist ein 4x4-Raster
   * mit sechzehn Zellen, und die Kette hat seit der Navigationskette neunzehn
   * Stufen. Ein Bauteil ohne Zelle zeichnet sich deshalb selbst — aus derselben
   * `draw`, die ohnehin als Rueckfall dasteht, in eine eigene Textur
   * (`gfx/textures.ts`). Das Raster zu vergroessern hiesse, ein **Bild** neu zu
   * erzeugen; das ist keine Codeaenderung, und bis dahin waere die Alternative
   * eine bezahlte Stufe, die niemand sieht — genau das, was FG-45 verhindert.
   */
  frame: number | null;
  /** Der eine Akzentton dieses Bauteils. */
  tint: number;
  /** Zeichnet mittig in eine bereits zentrierte Leinwand der Kantenlaenge `size`. */
  draw: (ctx: CanvasRenderingContext2D, size: number) => void;
}

// ── Zeichenhelfer ────────────────────────────────────────────────────────────
//
// Bewusst ohne Verlaeufe: der Pruefkontext von FG-45 zeichnet nichts, er
// **misst**, und ein `createRadialGradient` waere ein Objekt, das er nachbauen
// muesste, ohne dass eine einzige Form davon besser wuerde. Weiche Uebergaenge
// entstehen hier aus zwei Ringen mit niedrigem Alpha.

function box(ctx: CanvasRenderingContext2D, color: string, x: number, y: number, w: number, h: number): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
}

/** Eine Rumpfplatte: Schlagschatten, Flaeche, helle Oberkante. */
function plate(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  box(ctx, SHADOW, x + 2, y + 2, w, h);
  box(ctx, HULL, x, y, w, h);
  box(ctx, EDGE, x, y, w, 2);
}

function bar(
  ctx: CanvasRenderingContext2D, color: string,
  x1: number, y1: number, x2: number, y2: number, width: number,
): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

function disc(ctx: CanvasRenderingContext2D, color: string, x: number, y: number, r: number): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
}

function arcLine(
  ctx: CanvasRenderingContext2D, color: string,
  x: number, y: number, r: number, from: number, to: number, width: number, alpha = 1,
): void {
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.arc(x, y, r, from, to);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

/** Ein Vieleck aus flachen Koordinatenpaaren — kuerzer als sechs `lineTo`-Zeilen. */
function shape(ctx: CanvasRenderingContext2D, color: string, points: readonly number[]): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(points[0], points[1]);
  for (let i = 2; i < points.length; i += 2) ctx.lineTo(points[i], points[i + 1]);
  ctx.closePath();
  ctx.fill();
}

// ── Akt I: die Kapsel im Randfeld ────────────────────────────────────────────

/** Werkstatt — Anbau mit Presse und Kranarm. Der erste Handgriff am Rumpf. */
function drawWorkshop(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -34, -12, 52, 40);
  box(ctx, EDGE, -34, 14, 52, 6);
  // Die Presse: zwei Fuehrungssaeulen und ein Stempel dazwischen.
  box(ctx, SHADOW, -26, -4, 6, 26);
  box(ctx, SHADOW, -4, -4, 6, 26);
  box(ctx, EDGE, -28, 4, 32, 8);
  // Der Kranarm, zweigliedrig nach aussen-oben, mit Haken und Schweissfunke.
  bar(ctx, EDGE, 4, -10, 30, -34, 6);
  bar(ctx, EDGE, 30, -34, 46, -26, 4);
  bar(ctx, SHADOW, 46, -26, 46, -16, 2);
  box(ctx, '#f0c437', 12, -24, 10, 4);
  box(ctx, SHADOW, 16, -24, 3, 4);
  disc(ctx, '#ffd06a', 46, -14, 4);
}

/** Reaktorring — der zweite Kreis, von der Kante gesehen. */
function drawReactor(ctx: CanvasRenderingContext2D): void {
  ctx.strokeStyle = EDGE;
  ctx.lineWidth = 9;
  ctx.beginPath();
  ctx.ellipse(4, -4, 40, 22, 0, 0, TAU);
  ctx.stroke();
  ctx.strokeStyle = '#5fe6c8';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.ellipse(4, -4, 40, 22, 0, 0, TAU);
  ctx.stroke();
  disc(ctx, '#9ffff0', 4, -4, 5);
  // Sockel am Rumpf und zwei Kuehlrippen.
  plate(ctx, -12, 18, 32, 16);
  box(ctx, SHADOW, -6, 22, 4, 20);
  box(ctx, SHADOW, 8, 22, 4, 20);
}

/** Drohnenhangar — Plattform mit Tor und zwei Andockklauen. */
function drawHangar(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -40, -8, 80, 26);
  box(ctx, SHADOW, -16, -2, 32, 20);
  box(ctx, '#7ee0ff', -14, 0, 28, 4);
  // Die Klauen: je zwei nach innen zeigende Winkel.
  shape(ctx, EDGE, [-46, -20, -30, -20, -30, -12, -38, -12, -38, 6, -46, 6]);
  shape(ctx, EDGE, [46, -20, 30, -20, 30, -12, 38, -12, 38, 6, 46, 6]);
  // Eine Drohne auf der Plattform — dasselbe Motiv wie die `drone`-Textur.
  box(ctx, SHADOW, -8, -22, 16, 12);
  box(ctx, '#7ee0ff', -4, -19, 8, 6);
  box(ctx, EDGE, -12, -24, 4, 2);
  box(ctx, EDGE, 8, -24, 4, 2);
}

/** Sensorturm — Gittermast mit Parabolschuessel. Das hoechste Bauteil. */
function drawSensor(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -16, 28, 32, 14);
  bar(ctx, EDGE, -8, 34, -4, -18, 5);
  bar(ctx, EDGE, 8, 34, 4, -18, 5);
  for (let i = 0; i < 4; i++) {
    const y = 26 - i * 11;
    bar(ctx, SHADOW, -8 + i, y, 8 - i, y - 8, 2);
  }
  // Die Schuessel als Halbellipse, nach oben-aussen gekippt, mit Speisehorn.
  ctx.fillStyle = LIGHT;
  ctx.beginPath();
  ctx.ellipse(0, -30, 26, 13, -0.35, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = EDGE;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.ellipse(0, -30, 26, 13, -0.35, 0, TAU);
  ctx.stroke();
  bar(ctx, SHADOW, 0, -30, 14, -42, 3);
  disc(ctx, '#ffd84d', 15, -43, 4);
}

/** Kristalllabor — Glasmodul, in dem ein Kristall zur Linse geschliffen wird. */
function drawLab(ctx: CanvasRenderingContext2D): void {
  shape(ctx, SHADOW, [-30, -14, -14, -34, 16, -34, 32, -14, 16, 8, -14, 8]);
  shape(ctx, HULL, [-28, -14, -13, -32, 15, -32, 30, -14, 15, 6, -13, 6]);
  box(ctx, EDGE, -28, -16, 58, 3);
  // Der Kristall: Raute mit drei Schliffflaechen.
  shape(ctx, '#b98cff', [1, -28, 15, -13, 1, 2, -13, -13]);
  shape(ctx, '#e0d0ff', [1, -28, 15, -13, 1, -13]);
  // Linse und Strahl nach aussen.
  ctx.strokeStyle = '#e0d0ff';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.ellipse(30, -13, 4, 12, 0, 0, TAU);
  ctx.stroke();
  bar(ctx, '#e0d0ff', 15, -13, 48, -13, 2);
  plate(ctx, -18, 6, 36, 14);
}

/** Gravitationsfalle — zwei Feldspulen und ein Schacht, der nach innen faellt. */
function drawTrap(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -30, 26, 60, 14);
  // Zwei Feldspulen, aussen am Schacht.
  box(ctx, SHADOW, -36, -20, 10, 46);
  box(ctx, HULL, -34, -20, 8, 44);
  box(ctx, SHADOW, 26, -20, 10, 46);
  box(ctx, HULL, 26, -20, 8, 44);
  for (let i = 0; i < 3; i++) {
    box(ctx, '#c07ce0', -38, -14 + i * 14, 14, 4);
    box(ctx, '#c07ce0', 24, -14 + i * 14, 14, 4);
  }
  // Der Schacht: drei Boegen, nach innen enger und dichter — der Raum faellt.
  for (let i = 0; i < 3; i++) {
    arcLine(ctx, '#c07ce0', -2, 0, 30 - i * 9, Math.PI * 0.75, Math.PI * 2.25, 4 - i, 0.4 + i * 0.25);
  }
  // Ein gefangener Brocken kurz vor der Mitte, mit Fallstreifen.
  disc(ctx, EDGE, -2, 0, 4);
  bar(ctx, '#e0d0ff', 14, -12, 2, -2, 2);
}

/** Fokuswerk — Schleifstand mit Linse; zwei Strahlen treffen sich im Brennpunkt. */
function drawOptics(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -36, 18, 52, 16);
  box(ctx, SHADOW, -30, -8, 24, 28);
  box(ctx, HULL, -28, -8, 20, 26);
  box(ctx, EDGE, -28, -10, 20, 3);
  // Die Schleifscheibe an der Werkbank.
  disc(ctx, EDGE, -18, 4, 7);
  disc(ctx, SHADOW, -18, 4, 3);
  // Die Linse im Halter, aufrecht — im Blau der Fokuslinsen-Erzfarbe.
  ctx.strokeStyle = '#4d9cff';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.ellipse(6, -6, 5, 16, 0, 0, TAU);
  ctx.stroke();
  bar(ctx, SHADOW, 6, 10, 6, 20, 3);
  // Zwei Strahlen buendeln sich hinter der Linse im Brennpunkt.
  bar(ctx, '#9fd0ff', 11, -16, 40, -8, 2);
  bar(ctx, '#9fd0ff', 11, 4, 40, -8, 2);
  bar(ctx, '#e8f4ff', 40, -8, 50, -8, 2);
  disc(ctx, '#e8f4ff', 40, -8, 3);
}

/** Teilchenbeschleuniger — zwei Magnetpylonen und das sichtbare Ringsegment. */
function drawStargate(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -34, 24, 68, 16);
  for (const x of [-24, 16]) {
    box(ctx, SHADOW, x - 2, -30, 12, 58);
    box(ctx, HULL, x, -30, 8, 56);
    for (let i = 0; i < 3; i++) box(ctx, EDGE, x - 4, -22 + i * 16, 16, 5);
  }
  // Das Ringsegment zwischen den Pylonen — der Rest steht als Vektorring um
  // die ganze Station und ist zu gross, um ihn zu backen.
  arcLine(ctx, '#9ff0ff', 0, 30, 52, Math.PI * 1.18, Math.PI * 1.82, 6);
  arcLine(ctx, LIGHT, 0, 30, 52, Math.PI * 1.34, Math.PI * 1.66, 2);
  bar(ctx, SHADOW, -20, 24, -20, 40, 3);
  bar(ctx, SHADOW, 20, 24, 20, 40, 3);
}

/** Kartenwerk — ein Rechenwerk mit ausgefahrener Kartenplatte. */
function drawChartroom(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -30, 8, 60, 28);
  box(ctx, EDGE, -30, 30, 60, 5);
  // Die Kartenplatte: eine flach liegende Scheibe mit Gitternetz.
  shape(ctx, SHADOW, [-40, -12, 40, -12, 30, 8, -30, 8]);
  shape(ctx, HULL, [-38, -12, 38, -12, 29, 6, -29, 6]);
  for (let i = 1; i < 4; i++) {
    bar(ctx, EDGE, -38 + i * 19, -12, -29 + i * 14.5, 6, 1.5);
  }
  bar(ctx, EDGE, -34, -3, 34, -3, 1.5);
  // Zwei Markierungen und der Rechnerturm dahinter.
  disc(ctx, '#7ee0ff', -12, -6, 3.5);
  disc(ctx, '#7ee0ff', 16, 1, 3);
  box(ctx, SHADOW, -10, -34, 20, 22);
  box(ctx, HULL, -8, -34, 16, 20);
  for (let i = 0; i < 3; i++) box(ctx, '#7ee0ff', -5, -30 + i * 6, 10, 2);
}

/** Navigationsrechner — Kreiselring mit Nadel: er haelt eine Richtung. */
function drawAutopilot(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -26, 18, 52, 22);
  box(ctx, EDGE, -26, 34, 52, 5);
  // Zwei ineinanderliegende Kreiselringe.
  ctx.strokeStyle = EDGE; ctx.lineWidth = 5;
  ctx.beginPath(); ctx.ellipse(0, -8, 34, 34, 0, 0, TAU); ctx.stroke();
  ctx.strokeStyle = HULL; ctx.lineWidth = 2.5;
  ctx.beginPath(); ctx.ellipse(0, -8, 34, 34, 0, 0, TAU); ctx.stroke();
  ctx.strokeStyle = EDGE; ctx.lineWidth = 4;
  ctx.beginPath(); ctx.ellipse(0, -8, 22, 34, 0, 0, TAU); ctx.stroke();
  // Die Nadel: eine helle und eine dunkle Haelfte, wie beim Icon.
  shape(ctx, '#8ce05a', [0, -38, 8, -6, 0, -12]);
  shape(ctx, EDGE, [0, 22, -8, -10, 0, -4]);
  disc(ctx, LIGHT, 0, -8, 4);
}

/** Sprungspule — zwei Feldspulen und der gestreckte Raum dazwischen. */
function drawWarpcoil(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -34, 22, 68, 18);
  // Die Spulen als liegende Zylinder links und rechts.
  for (const x of [-40, 28]) {
    box(ctx, SHADOW, x - 2, -30, 16, 52);
    box(ctx, HULL, x, -30, 12, 50);
    for (let i = 0; i < 4; i++) box(ctx, '#b98cff', x - 3, -24 + i * 13, 18, 4);
  }
  // Der gestreckte Raum: drei Striche, nach innen heller und laenger.
  for (let i = 0; i < 3; i++) {
    const y = -18 + i * 18;
    bar(ctx, i === 1 ? '#e0d0ff' : '#b98cff', -22, y, 22, y, 4 - i * 0.8);
  }
  disc(ctx, '#e8e0ff', 0, 0, 5);
}

// ── Akt II: der Wiederaufbau im Heimatsystem ─────────────────────────────────

/** Bergungswerft — der Kran, der die Truemmer der alten Basis zerlegt. */
function drawDebris(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -30, 22, 60, 16);
  // Die geborgene Huellenplatte kommt **zuerst** und hell, die Traeger liegen
  // dunkel darueber. Andersherum verschwand sie im Geruest: zwei helle Formen
  // uebereinander sind eine, und die Werft haette nichts in der Hand.
  shape(ctx, HULL, [-22, -26, 18, -21, 23, 6, 9, -1, 3, 12, -9, 1, -19, 8]);
  shape(ctx, SHADOW, [-22, -26, 18, -21, 16, -13, -20, -18]);
  bar(ctx, EDGE, -12, -19, -5, 4, 2);
  bar(ctx, EDGE, 7, -17, 12, -1, 2);
  // Zwei gekreuzte Traeger und die Laufkatze darueber.
  bar(ctx, RUST, -24, 26, 22, -30, 7);
  bar(ctx, RUST, 24, 26, -22, -30, 7);
  bar(ctx, EDGE, -22, -32, 22, -32, 5);
  // Der Schneidbrenner mit kurzem Flammenkegel.
  box(ctx, EDGE, 26, -14, 14, 6);
  shape(ctx, '#ff9a3c', [26, -14, 26, -8, 10, -11]);
}

/** Kuppeldichtung — die Kuppel haelt wieder Druck. Sitzt auf dem Rumpf. */
function drawDome(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = SHADOW;
  ctx.beginPath();
  ctx.ellipse(0, 12, 48, 40, 0, Math.PI, TAU);
  ctx.fill();
  ctx.fillStyle = '#4fb8c8';
  ctx.beginPath();
  ctx.ellipse(0, 12, 44, 36, 0, Math.PI, TAU);
  ctx.fill();
  // Drei Dichtungsrippen als Meridiane.
  bar(ctx, EDGE, 0, -25, 0, 12, 4);
  bar(ctx, EDGE, -26, -14, -30, 12, 4);
  bar(ctx, EDGE, 26, -14, 30, 12, 4);
  // Ein Streifreflex auf dem Glas und der umlaufende Dichtungsring.
  arcLine(ctx, '#a8e8f0', 0, 12, 32, Math.PI * 1.15, Math.PI * 1.42, 4, 0.75);
  box(ctx, EDGE, -48, 10, 96, 6);
  box(ctx, LIGHT, -48, 10, 96, 2);
}

/** Schmelzwerk — aus Bergungsstahl wird wieder Baustoff. */
function drawFoundry(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -30, -14, 52, 42);
  box(ctx, SHADOW, -30, 16, 52, 5);
  // Der schraege Kamin mit drei Rauchpixeln.
  shape(ctx, EDGE, [4, -14, 18, -14, 30, -38, 20, -40]);
  box(ctx, SHADOW, 22, -38, 8, 3);
  for (let i = 0; i < 3; i++) disc(ctx, EDGE, 26 + i * 4, -42 - i * 4, 3 - i * 0.5);
  // Abstichloch, Strahl und Pfanne.
  box(ctx, '#ff7a3c', -24, 0, 12, 8);
  bar(ctx, '#ff7a3c', -18, 8, -26, 26, 4);
  shape(ctx, SHADOW, [-38, 24, -14, 24, -18, 38, -34, 38]);
  box(ctx, '#ff7a3c', -34, 25, 16, 4);
}

/** Resonanzkammer — Schmelzglas buendelt, was der Bruch verstreut hat. */
function drawResonator(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -16, 6, 34, 30);
  box(ctx, EDGE, -12, -28, 8, 36);
  box(ctx, EDGE, 6, -28, 8, 36);
  box(ctx, HULL, -12, -6, 26, 10);
  disc(ctx, '#9ff0ff', 1, -1, 5);
  // Drei Boegen, nach aussen duenner und blasser — der gebuendelte Klang.
  for (let i = 0; i < 3; i++) {
    arcLine(ctx, '#9ff0ff', 1, -1, 20 + i * 12, Math.PI * 1.18, Math.PI * 1.82, 5 - i, 0.85 - i * 0.22);
  }
}

/** Heimatfeuer — das kleinste Motiv und das hellste. Das System traegt wieder. */
function drawHomefire(ctx: CanvasRenderingContext2D): void {
  shape(ctx, SHADOW, [-16, 42, 16, 42, 11, -6, -11, -6]);
  shape(ctx, HULL, [-14, 40, 14, 40, 9, -6, -9, -6]);
  box(ctx, EDGE, -16, -8, 32, 7);
  box(ctx, SHADOW, -12, 12, 24, 3);
  // Der offene Feuerkorb und die stehende Flamme.
  shape(ctx, EDGE, [-15, -16, 15, -16, 11, -8, -11, -8]);
  shape(ctx, '#ffd06a', [0, -44, 9, -24, 0, -16, -9, -24]);
  shape(ctx, '#fff4d0', [0, -38, 4, -25, 0, -20, -4, -25]);
  // Der warme Halo aus zwei Ringen statt eines Verlaufs.
  arcLine(ctx, '#ffc46a', 0, -27, 18, 0, TAU, 3, 0.3);
  arcLine(ctx, '#ffc46a', 0, -27, 26, 0, TAU, 2, 0.16);
}

/** Relaisnetz — ein Mast mit drei Bojenschalen an einer Kette. */
function drawRelay(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -22, 20, 44, 18);
  box(ctx, EDGE, -22, 32, 44, 5);
  bar(ctx, EDGE, 0, 22, 0, -26, 6);
  // Drei Schalen, nach oben kleiner — die Kette reicht weiter, als man sieht.
  for (let i = 0; i < 3; i++) {
    const y = 6 - i * 18;
    const r = 22 - i * 6;
    arcLine(ctx, i === 0 ? EDGE : HULL, 0, y, r, Math.PI * 1.12, Math.PI * 1.88, 4);
    disc(ctx, '#5fe6c8', 0, y - r + 4, 3 - i * 0.4);
  }
  bar(ctx, SHADOW, -14, 12, 14, 12, 2);
}

/** Umschlagkran — der Ladekran ueber der Werft, mit haengender Kiste. */
function drawCrane(ctx: CanvasRenderingContext2D): void {
  plate(ctx, -30, 24, 54, 16);
  box(ctx, EDGE, -30, 34, 54, 5);
  // Mast mit Querstreben und der Ausleger darueber.
  box(ctx, SHADOW, -18, -30, 14, 56);
  box(ctx, HULL, -16, -30, 10, 54);
  for (let i = 0; i < 4; i++) bar(ctx, SHADOW, -16, -24 + i * 13, -6, -17 + i * 13, 2);
  bar(ctx, '#ffd84d', -20, -34, 40, -34, 6);
  bar(ctx, EDGE, -11, -30, 34, -46, 3);
  // Laufkatze, Seil und die Kiste am Haken.
  box(ctx, EDGE, 24, -38, 10, 6);
  bar(ctx, SHADOW, 29, -32, 29, -12, 2);
  box(ctx, SHADOW, 18, -12, 22, 20);
  box(ctx, HULL, 20, -12, 18, 18);
  box(ctx, '#ffd84d', 20, -5, 18, 4);
}

/** Sprungbake — ein Pylon, gegen den sich das Feld ausrichtet. */
function drawBeacon(ctx: CanvasRenderingContext2D): void {
  shape(ctx, SHADOW, [-20, 40, 20, 40, 12, -4, -12, -4]);
  shape(ctx, HULL, [-18, 38, 18, 38, 10, -4, -10, -4]);
  box(ctx, EDGE, -20, -8, 40, 6);
  // Der Kopf: ein Kristallsegment zwischen zwei Klauen.
  shape(ctx, EDGE, [-16, -10, -8, -10, -6, -34, -14, -34]);
  shape(ctx, EDGE, [16, -10, 8, -10, 6, -34, 14, -34]);
  shape(ctx, '#ff9a3c', [0, -46, 9, -26, 0, -14, -9, -26]);
  shape(ctx, '#ffd8a8', [0, -40, 4, -27, 0, -20, -4, -27]);
  // Zwei Ausrichtungsringe, nach aussen blasser. Beide bleiben innerhalb von
  // `BASE_MOTIF_REACH` — ein beschnittenes Bauteil sieht aus wie ein
  // Zeichenfehler, nicht wie eine zu grosse Form (FG-45).
  arcLine(ctx, '#ff9a3c', 0, -28, 18, 0, TAU, 3, 0.34);
  arcLine(ctx, '#ff9a3c', 0, -28, 26, 0, TAU, 2, 0.18);
}

/**
 * Die Registry — Schluessel ist die **Build-ID**.
 *
 * Sie steht ohnehin stabil in der Tabelle und in jedem Spielstand; ein
 * zusaetzliches `module`-Feld in `tables.py` waere ein String, den das Backend
 * nie liest und der nur durch den Abzug wandert, um hier nachgeschlagen zu
 * werden. Bei den Icons ist das anders — dort teilen sich beide Sprachen einen
 * **Namen**. Hier gibt es nichts zu teilen.
 *
 * Die Anker liegen **fest** und **am Rumpf**, nicht auf einem weiten Kreis: ein
 * Bauteil, das frei daneben schwebt, sieht nach einem zweiten Objekt aus, nicht
 * nach einem Anbau. Sie folgen deshalb der Rumpfform — die Flanken weiter
 * aussen als oben und unten, weil der Rumpf breiter als hoch ist.
 *
 * Innerhalb einer Aera sind sie weit genug auseinander, dass kein Bauteil ein
 * anderes verdeckt — dieselbe Falle wie zwei Baumknoten auf derselben
 * Gitterposition (FG-22). Gate FG-45 misst beides nach.
 */
export const BASE_MODULES: Record<string, BaseModule> = {
  // Akt I — die Flanken, dann unten, dann oben. Die Linsenkette fuellt die
  // unteren Flankenluecken: neue Anker, keine verschobenen (FG-45).
  //
  // Die Frames folgen der **Tabellenreihenfolge**, nicht der hier: so steht die
  // Reihe im Bild in derselben Folge wie im Prompt in `SPRITES.md`, und wer das
  // Blatt neu erzeugt, kann es Zelle fuer Zelle nachzaehlen.
  workshop: { x: 122, y: 10, frame: 0, tint: 0xffd06a, draw: drawWorkshop },
  reactor: { x: -118, y: 6, frame: 1, tint: 0x5fe6c8, draw: drawReactor },
  hangar: { x: -44, y: 78, frame: 2, tint: 0x7ee0ff, draw: drawHangar },
  lab: { x: 52, y: 86, frame: 4, tint: 0xb98cff, draw: drawLab },
  trap: { x: -125, y: -72, frame: 5, tint: 0xc07ce0, draw: drawTrap },
  optics: { x: 122, y: -68, frame: 6, tint: 0x4d9cff, draw: drawOptics },
  sensor: { x: 34, y: -76, frame: 3, tint: 0xffd84d, draw: drawSensor },
  stargate: { x: -46, y: -74, frame: 7, tint: 0x9ff0ff, draw: drawStargate },
  // Die Navigationskette. Ihre Anker liegen aussen: drei weitere Bauteile
  // passen nur noch dorthin, ohne den Mindestabstand von 72 zu unterschreiten.
  // Drei alte sind dafuer um wenige Einheiten gerueckt (Hangar, Falle,
  // Fokuswerk) — zusammen 16 Einheiten Versatz, gemessen, nicht geschaetzt.
  //
  // Sie belegen die **drei letzten Zellen** des Blattes; danach ist es voll,
  // und die Kette von Akt II zeichnet sich selbst (`frame: null`).
  chartroom: { x: -1, y: -146, frame: 13, tint: 0x7ee0ff, draw: drawChartroom },
  autopilot: { x: -7, y: 147, frame: 14, tint: 0x8ce05a, draw: drawAutopilot },
  warpcoil: { x: -122, y: 84, frame: 15, tint: 0xb98cff, draw: drawWarpcoil },
  // Akt II.
  debris: { x: -112, y: 30, frame: 8, tint: 0xff9a3c, draw: drawDebris },
  foundry: { x: 112, y: 34, frame: 10, tint: 0xff7a3c, draw: drawFoundry },
  resonator: { x: -48, y: 84, frame: 11, tint: 0x9ff0ff, draw: drawResonator },
  dome: { x: -4, y: -70, frame: 9, tint: 0x4fb8c8, draw: drawDome },
  relay: { x: 32, y: 130, frame: null, tint: 0x5fe6c8, draw: drawRelay },
  crane: { x: -106, y: -59, frame: null, tint: 0xffd84d, draw: drawCrane },
  beacon: { x: 142, y: -41, frame: null, tint: 0xff9a3c, draw: drawBeacon },
  homefire: { x: 72, y: -88, frame: 12, tint: 0xffc46a, draw: drawHomefire },
};

/** Alle bekannten Bauteile — exportiert, damit FG-45 sie gegen die Tabelle haelt. */
export const BASE_MODULE_IDS: readonly string[] = Object.keys(BASE_MODULES);

export const baseModule = (id: string): BaseModule | undefined => BASE_MODULES[id];

/**
 * Die Ausbaukette **einer** Aera, in Tabellenreihenfolge.
 *
 * Schreibt in ein uebergebenes Zielarray: die Szene ruft das je Bild (FG-14).
 */
export function chainBuilds(era: number, out: string[]): string[] {
  out.length = 0;
  for (const entry of builds()) {
    if (entry.minEra === era) out.push(entry.id);
  }
  return out;
}

/** Wie lang die Kette dieser Aera ist. */
export function chainLength(era: number): number {
  let count = 0;
  for (const entry of builds()) {
    if (entry.minEra === era) count++;
  }
  return count;
}

/**
 * Wie viele Stufen **dieser** Kette stehen.
 *
 * `save.built` zaehlt ueber die Naht hinweg weiter (8 → 13), damit kein
 * Spielstand angefasst werden muss. Nach dem Sprung steht deshalb ein
 * Truemmerfeld, und der Wiederaufbau faengt wieder bei null an.
 */
export function chainLevel(era: number, built: number): number {
  let before = 0;
  for (const entry of builds()) {
    if (entry.minEra < era) before++;
  }
  return Math.max(0, Math.min(chainLength(era), Math.floor(built) - before));
}

/** Die laengste Kette ueber alle Aeren — so viele Modulbilder braucht die Szene. */
export function maxChainLength(): number {
  let longest = 0;
  for (const entry of eras()) longest = Math.max(longest, chainLength(entry.era));
  return longest;
}

/** Rumpfradius zu einer Stufe. Beide Zeichenwege lesen dieselbe Rechnung. */
export const hullRadius = (level: number): number =>
  BASE_HULL_R0 + Math.max(0, level) * BASE_HULL_GROW;

/**
 * Farbe des Abschlussrings — die des Abschlussbauteils **dieser** Aera.
 *
 * Aus der Registry gelesen statt in eine zweite Tabelle geschrieben: sonst
 * saehe der Ring anders aus als das Bauteil, das ihn hinstellt, und niemand
 * meldete es.
 */
export function finalTint(era: number): number {
  for (const entry of builds()) {
    if (entry.minEra !== era || !entry.final) continue;
    const modul = BASE_MODULES[entry.id];
    if (modul) return modul.tint;
  }
  return BASE_HULL_TINT;
}
